import React, { useState, useMemo } from 'react';
import type { LookalikeRow } from '../../../store/investor/apiHelpers';
import { STATUS_COLORS, COLUMNS, SortKey, SortDir } from './constants';
import styles from './LookalikeTable.module.scss';

export const useLookalikeTable = (lookalikes: LookalikeRow[]) => {
    const [sortKey, setSortKey] = useState<SortKey | null>(null);
    const [sortDir, setSortDir] = useState<SortDir>('asc');

    const handleSort = (key: string) => {
        const col = COLUMNS.find((c) => c.key === key);
        if (!col?.sortable) return;
        if (sortKey === key) {
            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(key as SortKey);
            setSortDir('asc');
        }
    };

    const sortedLookalikes = useMemo(() => {
        if (!sortKey) return lookalikes;
        return [...lookalikes].sort((a, b) => {
            const aVal = (a as any)[sortKey];
            const bVal = (b as any)[sortKey];
            if (aVal == null && bVal == null) return 0;
            if (aVal == null) return 1;
            if (bVal == null) return -1;
            const cmp = typeof aVal === 'number' ? aVal - bVal : String(aVal).localeCompare(String(bVal));
            return sortDir === 'asc' ? cmp : -cmp;
        });
    }, [lookalikes, sortKey, sortDir]);

    const formatInvestors = (investors: string | null): React.ReactNode => {
        if (!investors) return 'NA';

        const parts = investors
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);

        if (parts.length === 0) return 'NA';

        if (parts.length === 1) {
            return <span className={styles.investorBadge}>{parts[0]}</span>;
        }

        return (
            <span className={styles.investorsCell}>
                <span className={styles.investorBadge}>{parts[0]}</span>
                <span className={styles.moreInvestors}>+{parts.length - 1} more</span>
            </span>
        );
    };

    return { sortKey, sortDir, handleSort, sortedLookalikes, formatInvestors };
};
